import { useTranslation } from "react-i18next";

import type { IWidgetRenderContext } from "@/components/dashboard-grid";
import { useGetOnLeaveQuery } from "@/services/(company)/leaveRequestApi";
import { useGetCompanyTimeAttendanceStatsQuery } from "@/services/(company)/api.time-attendance";
import { PATHS } from "@/routes/paths";
import { useLocale } from "@/hooks/useLocale";
import {
  AvatarStack,
  HeroLine,
  MetricValue,
  MiniMetric,
  PersonRow,
  RelativeTime,
  RowsSkeleton,
  WidgetEmpty,
  WidgetNoAccess,
} from "./primitives";
import { useServerToday } from "@/hooks/useServerToday";

/** Rows each list size shows without clipping. `grand` is `xl`'s width with a third row of height. */
const LIST_ROWS = { lg: 3, xl: 4, grand: 8 } as const;

/** Who is out today — a count, a face pile, or a list, depending on the size. */
export const OnLeaveTodayWidget = ({
  size,
  isEditing,
}: IWidgetRenderContext) => {
  const { t } = useTranslation("company");
  const { formatDate } = useLocale();
  // Server-clock date, stable for the whole day so the query key does not churn.
  const today = useServerToday();

  const { data, isLoading, error } = useGetOnLeaveQuery({
    start: today,
    end: today,
    limit: 10,
  });
  // The list is capped at ten; `totalOnLeave` is the real figure. Counting the
  // array — as this widget used to — reported ten for a company with fifteen
  // people out.
  const {
    data: attendanceStats,
    isLoading: isAttendanceLoading,
    error: attendanceError,
  } = useGetCompanyTimeAttendanceStatsQuery({});

  // The leave list is this widget's own gate. Without it there is neither a
  // count nor a list, so the whole body becomes the refusal.
  if (error) return <WidgetNoAccess error={error} />;

  // The total comes from time-attendance, which this widget was never gated on.
  // Losing it means there is no true total — and the old `?? people.length`
  // fallback would quietly print the ten-row page cap as if it were one. The
  // same fallback fires during the ordinary load window (the leave list
  // typically resolves before the stats do), so the loading state has to cover
  // both queries, not just the list's — otherwise the cap flashes as the real
  // total on every cold open.
  const noTotal = Boolean(attendanceError);
  const isTotalLoading = isLoading || isAttendanceLoading;
  const people = data?.data ?? [];
  const total = attendanceStats?.data?.totalOnLeave ?? people.length;
  const avatars = people.map((person) => ({
    id: person.userId,
    name: person.fullName,
    image: person.profileImage,
  }));

  // Who comes back first. "Everyone's out" is a count; "Sara is back Thursday"
  // is the thing a manager reading this actually needs. Sorted by end date
  // rather than trusting the list's own order, which is not specified to be.
  const nextBack = [...people].sort(
    (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
  )[0];

  // The card draws the chip and the title at every size, this one included, so
  // there is nothing here to add beyond the number.
  if (size === "xs") {
    return (
      <MiniMetric
        value={total}
        isLoading={isTotalLoading}
        noAccess={noTotal}
        tone="info"
      />
    );
  }

  if (size === "sm") {
    return (
      <div className="flex h-full flex-col justify-center gap-0.5">
        <MetricValue
          value={total}
          isLoading={isTotalLoading}
          noAccess={noTotal}
          tone="info"
        />
        <p className="text-[11px] text-muted-foreground">
          {noTotal
            ? t("company:widgets.count_unavailable")
            : t("company:widgets.person_away", { count: total })}
        </p>
      </div>
    );
  }

  if (size === "md") {
    // Guarded on `noTotal` and `isTotalLoading`: with the count unreadable or
    // still in flight, `total` is either not final or has fallen back to the
    // list length, and neither an empty *page* nor an incomplete read is proof
    // that nobody is out.
    if (total === 0 && !isTotalLoading && !noTotal) {
      return (
        <WidgetEmpty message={t("company:widgets.everyone_is_in_today")} />
      );
    }

    // One row's worth of body height at `md`, so the face pile and the total
    // share `HeroLine`'s row as trailing accessories instead of stacking a
    // second row beneath it.
    return (
      <div className="flex h-full items-center">
        <HeroLine
          isLoading={isLoading}
          avatar={
            nextBack && {
              name: nextBack.fullName,
              image: nextBack.profileImage,
            }
          }
          primary={nextBack?.fullName ?? ""}
          caption={
            nextBack ? (
              <RelativeTime
                date={nextBack.endDate}
                prefix={t("company:widgets.back")}
              />
            ) : undefined
          }
          trailing={
            <span className="flex items-center gap-2">
              <AvatarStack
                people={avatars}
                max={3}
                showOverflowLabel={false}
                size="sm"
                isLoading={isLoading}
              />
              <MetricValue
                value={total}
                isLoading={isTotalLoading}
                noAccess={noTotal}
                tone="info"
                scale="md"
              />
            </span>
          }
        />
      </div>
    );
  }

  // `size` is typed against every size in the system; this widget offers three
  // of the seven, so the map has no entry for the rest.
  const rows = LIST_ROWS[size as keyof typeof LIST_ROWS] ?? LIST_ROWS.lg;

  return (
    <div className="flex h-full min-h-0 flex-col gap-1.5">
      <div className="flex shrink-0 items-center gap-3">
        <MetricValue
          value={total}
          isLoading={isTotalLoading}
          noAccess={noTotal}
          tone="info"
          scale="md"
        />
        <AvatarStack
          people={avatars}
          max={4}
          showOverflowLabel={false}
          size="sm"
          isLoading={isLoading}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-hidden border-t border-border pt-1.5">
        {isLoading ? (
          <RowsSkeleton rows={rows} />
        ) : people.length === 0 ? (
          <WidgetEmpty
            message={t("company:widgets.no_approved_leave_overlaps")}
          />
        ) : (
          <ul className="flex flex-col">
            {people.slice(0, rows).map((person) => (
              <li key={person.leaveId}>
                <PersonRow
                  name={person.fullName}
                  image={person.profileImage}
                  primary={person.fullName}
                  secondary={
                    person.startDate === person.endDate
                      ? (person.designation ??
                        formatDate(new Date(person.endDate), "dd MMM yyyy"))
                      : `${formatDate(new Date(person.startDate), "dd MMM yyyy")} – ${formatDate(new Date(person.endDate), "dd MMM yyyy")}`
                  }
                  to={PATHS.COMPANY.COMPANY_LEAVES_MANAGEMENT_PATHS.LEAVES_SUMMARY(
                    person.leaveId,
                  )}
                  isEditing={isEditing}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
